import Cookies from 'js-cookie'
import axios from 'axios'

const UTM_COOKIE_NAME = import.meta.env.VITE_COOKIE_UTM || 'mktg_track'
const SEM_COOKIE_NAME = import.meta.env.VITE_COOKIE_SEM || 'mktg_adw_data'
const UTM_EXPIRE_DAYS = Number(import.meta.env.VITE_COOKIE_UTM_EXPIRE_DAYS || 180)
const UTM_UNWRITABLE_HOURS = Number(import.meta.env.VITE_COOKIE_UTM_UNWRITABLE_HOURS || 24)
const SEM_EXPIRE_DAYS = Number(import.meta.env.VITE_COOKIE_SEM_EXPIRE_DAYS || 30)
const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN || ''
const API_URL = import.meta.env.VITE_API_URL || 'https://webapi.staging.translated.com'

const cookieDomainOpts = COOKIE_DOMAIN ? {domain: COOKIE_DOMAIN, path: '/'} : {}

console.log('[marketing-cookies]', {
  [UTM_COOKIE_NAME]: Cookies.get(UTM_COOKIE_NAME),
  [SEM_COOKIE_NAME]: Cookies.get(SEM_COOKIE_NAME),
  search: window.location.search,
})

// Prevent multiple initializations
if (window.marketingCookiesInitialized) {
  console.warn('Marketing cookies already initialized, skipping...')
} else {
  window.marketingCookiesInitialized = true

  // marketing cookies - you can clear cookies with Object.keys(Cookies.get()).forEach(cookieName => Cookies.remove(cookieName))
  const search = Object.fromEntries(new URLSearchParams(window.location.search))

  const getCookieValues = (name) =>
    document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .filter((cookie) => cookie.startsWith(`${name}=`))
      .map((cookie) => {
        const value = cookie.substring(name.length + 1)
        try {
          return decodeURIComponent(value)
        } catch (error) {
          return value
        }
      })

  const getBestTrackingCookieValue = (name) => {
    const values = getCookieValues(name)
    if (values.length === 0) return null

    return values.reduce((bestValue, currentValue) => {
      const bestScore = bestValue.split(',').filter(Boolean).length
      const currentScore = currentValue.split(',').filter(Boolean).length
      return currentScore > bestScore ? currentValue : bestValue
    })
  }

  const normalizeTrackingCookieDomain = (name, expires) => {
    if (!COOKIE_DOMAIN) return

    const value = getBestTrackingCookieValue(name)
    if (!value) return

    Cookies.remove(name)
    Cookies.set(name, value, {
      expires,
      ...cookieDomainOpts,
    })
  }

  normalizeTrackingCookieDomain(UTM_COOKIE_NAME, UTM_EXPIRE_DAYS)
  normalizeTrackingCookieDomain(SEM_COOKIE_NAME, SEM_EXPIRE_DAYS)

  /*
     == utm cookie strategy ==
     - expire every COOKIE_UTM_EXPIRE_DAYS days
     - it is immutable for the first COOKIE_UTM_UNWRITABLE_HOURS from its creation time
     -- after COOKIE_UTM_UNWRITABLE_HOURS passed, it is allowed to modify the cookie content
  */
  const now = Date.now()
  let cookieUtm = [
    search.utm_medium,
    search.utm_source,
    search.utm_campaign,
    search.utm_custom,
    search.utm_term,
    search.utm_content,
    search.refid,
    document.referrer,
    search.gclid,
    now,
    search.gbraid,
    search.wbraid,
  ]

  // Spec: touch the cookie only if the URL carries at least one tracking
  // parameter (utm_*, gclid, refid, gbraid, wbraid). document.referrer and the
  // timestamp are not tracking params, so they must not trigger a write.
  const hasTrackingParam = Boolean(
    search.utm_medium ||
      search.utm_source ||
      search.utm_campaign ||
      search.utm_custom ||
      search.utm_term ||
      search.utm_content ||
      search.refid ||
      search.gclid ||
      search.gbraid ||
      search.wbraid,
  )

  if (hasTrackingParam) {
    cookieUtm = cookieUtm.join(',')

    if (!Cookies.get(UTM_COOKIE_NAME)) {
      Cookies.set(UTM_COOKIE_NAME, cookieUtm, {expires: UTM_EXPIRE_DAYS, ...cookieDomainOpts})
    } else {
      let currentCookieUtm = Cookies.get(UTM_COOKIE_NAME).split(',')
      // position 10 (zero-indexed 9) holds the creation timestamp; gbraid/wbraid follow
      let utmCreationDate = currentCookieUtm[9]
      const creationDateDiffHours = Math.abs(now - Number(utmCreationDate)) / 36e5

      if (creationDateDiffHours >= UTM_UNWRITABLE_HOURS) {
        Cookies.set(UTM_COOKIE_NAME, cookieUtm, {expires: UTM_EXPIRE_DAYS, ...cookieDomainOpts})
      }
    }
  }

  /*
     == sem cookie strategy ==
     - expire every COOKIE_SEM_EXPIRE_DAYS days
     - it is always rewritable if new values come from query string
     -- partial updates allowed: if some data changes, overwrite the changed data and keep the rest
  */
  let cookieSem = [
    search.sem_ad,
    search.sem_kw,
    search.sem_pos,
  ]

  if (cookieSem.some(Boolean)) {
    if (!Cookies.get(SEM_COOKIE_NAME)) {
      cookieSem.push(now)
      cookieSem = cookieSem.join(',')
      Cookies.set(SEM_COOKIE_NAME, cookieSem, {expires: SEM_EXPIRE_DAYS, ...cookieDomainOpts})
    } else {
      let currentCookieSem = Cookies.get(SEM_COOKIE_NAME).split(',')
      let cookieSemTimestamp = currentCookieSem.pop()

      cookieSem.forEach((element, index, array) => {
        if (index === 1 && element.match(/translated/gi)) {
          array[index] = currentCookieSem[index]
          return
        }

        if (!element || !element.trim()) {
          array[index] = currentCookieSem[index]
        } else {
          cookieSemTimestamp = now
        }
      })

      cookieSem.push(cookieSemTimestamp)
      cookieSem = cookieSem.join(',')
      Cookies.set(SEM_COOKIE_NAME, cookieSem, {expires: SEM_EXPIRE_DAYS, ...cookieDomainOpts})
    }
  }

  // Transcreation override
  const COOKIE_TRANSCREATION_OVERRIDE_NAME = 'transcreation-o'
  const COOKIE_TRANSCREATION_OVERRIDE_TTL_DAYS = 30
  const transcreationOverride = search['q']
  if (transcreationOverride) {
    Cookies.set(COOKIE_TRANSCREATION_OVERRIDE_NAME, transcreationOverride, {
      expires: COOKIE_TRANSCREATION_OVERRIDE_TTL_DAYS,
    })
  }

  // intercom tags
  if (search.int_e && search.int_t) {
    const params = {email: search.int_e, tag: search.int_t}
    axios.get(API_URL + '/customer/setIntercomTag', {params})
  }
}

export default function initMarketingCookies() {
  // This function can be called to ensure initialization
  // The actual initialization happens when the module is imported
} 